import { useEffect, useState } from "react";

/**
 * A clock that ticks on the minute, for the times shown beside a time zone (FR-50).
 *
 * Aligned to the wall clock rather than to mount: a label reading `9:42 AM` must change the
 * moment the minute does, and a plain one-minute interval started at a random point in the
 * minute is on average thirty seconds stale — which is exactly the half-minute during which the
 * screen and the clock on the wall disagree.
 *
 * `active` is the escape hatch for a screen that is not looking at it: a timer that keeps a
 * component re-rendering behind a closed panel costs battery and buys nothing.
 */

const MS_PER_MINUTE = 60_000;

export function useMinuteClock(active = true): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!active) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (from: number): void => {
      timer = setTimeout(
        () => {
          const at = new Date();
          setNow(at);
          schedule(at.getTime());
        },
        MS_PER_MINUTE - (from % MS_PER_MINUTE),
      );
    };
    schedule(Date.now());
    return () => clearTimeout(timer);
  }, [active]);

  return now;
}
